/**
 * `createEventDispatcher` · Round D — two-mechanism NATS event fanout.
 *
 * SPEC ANCHOR: file 05 §L246-276 (FR 3) + §L2634 (FR 10 idempotency).
 *
 * The dispatcher is the MessageHandler returned to <SDKProvider> that
 * `useNatsSubscription` invokes for every delivered + seq-deduped event.
 * It performs two parallel fanouts:
 *
 *   1. Routing-table fanout — codegen-emitted `routingTable` maps subject
 *      patterns to cache-write closures (`setQueriesData` / `invalidateQueries`).
 *      Default is an empty Map — works fine before codegen lands; runtime
 *      simply skips the routing-write path.
 *
 *   2. Registry-callback fanout — every hook that acquired interest in a
 *      subject (via `registry.acquireWhenVisible`) registered an
 *      `applyEvent` closure on the SubjectRegistry's trie. The dispatcher
 *      walks the trie for matching subjects and invokes each closure.
 *
 * APPLYEVENT ERROR CONTAINMENT (FR 11 · plan-side D5)
 * --------------------------------------------------
 * A throwing `applyEvent` MUST NOT kill the dispatch loop. Every callback
 * invocation is wrapped in try/catch. On throw the dispatcher:
 *   - emits an `onError` notification (telemetry sink)
 *   - bumps the per-subject failure counter
 *   - on 5 failures in a 60s window, quarantines the subject for 60s
 *     (events on that subject are silently dropped during quarantine)
 *
 * IDEMPOTENCY (FR 10)
 * -------------------
 * `idempotencyLru` (optional) lets `patch:set` / `invalidate` routes
 * suppress re-delivered NATS events. The dispatcher consults
 * `lru.has(key)` for each routing-table entry that declares an
 * idempotency-key field. `patch:append` BYPASSES — append routes need
 * exactly-once via server-emitted append-key (see `idempotency-lru.ts`).
 */

import type { QueryClient } from '@tanstack/react-query';

import type { IdempotencyLRU } from '../../nats/registry/idempotency-lru.js';
import {
  getRegistrySingleton,
  type SubjectRegistryApi,
} from '../../nats/registry/subject-registry.js';
import type { MessageHandler } from '../../provider/useNatsSubscription.js';

export type RoutingMode = 'patch:set' | 'patch:append' | 'invalidate';

export interface RoutingEntryContext {
  /**
   * Predicate gating list-queryKey dispatch. Routes that update list-shaped
   * cache rows (e.g. `setQueryData` on a `useGetMarkets` key) should
   * consult this before writing per-queryKey. Returns `true` when the
   * queryKey is either (a) not opted into viewport gating, or (b) opted
   * in AND currently intersecting the viewport. Returns `false` ONLY when
   * the queryKey has called `registry.acquireListVisibility` AND is
   * offscreen — write should be skipped to avoid waking offscreen lists.
   */
  readonly isListQueryKeyDispatchAllowed: (queryHash: string) => boolean;
}

export interface RoutingEntry {
  /**
   * Cache operation invoked for matching events. Receives the original
   * `subject` so routes keyed by a wildcard pattern can recover the
   * concrete `<id>` segment.
   */
  readonly apply: (
    qc: QueryClient,
    data: Uint8Array,
    subject: string,
    seq: number,
    ctx: RoutingEntryContext,
  ) => void;
  /** Operation type — gates idempotency-LRU consultation. */
  readonly mode: RoutingMode;
  /** Optional extractor that pulls an idempotency key from the payload. */
  readonly extractIdempotencyKey?: (data: Uint8Array) => string | undefined;
}

export interface DispatcherDeps {
  readonly qc: QueryClient;
  readonly registry: SubjectRegistryApi;
  /** Map keyed by exact subject OR wildcard pattern (e.g. `morfi.market.*.trade`). */
  readonly routingTable?: ReadonlyMap<string, RoutingEntry>;
  readonly idempotencyLru?: IdempotencyLRU;
  /**
   * Predicate gating route fanout — by default, all routes fire. Codegen
   * may inject a predicate that consults `meta.realtime` per-query.
   */
  readonly routeGate?: (subject: string) => boolean;
  /**
   * Error sink — called once per uncaught throw from a routing entry OR
   * a registry-callback `applyEvent`. Non-blocking; teardown not allowed.
   */
  readonly onError?: (
    err: unknown,
    ctx: { readonly subject: string; readonly seq: number },
  ) => void;
}

/**
 * NATS-style wildcard match.
 *   - `*` matches a single subject token (no dots).
 *   - `>` matches one-or-more trailing tokens.
 * Anything else is literal.
 *
 * Exported so the realtime apply path (`useRealtimeQuery`) can recover the
 * subject PATTERN a concrete event matched — the sequence-gate keys per
 * `(queryKey, subjectPattern)`.
 */
export function matchSubject(pattern: string, subject: string): boolean {
  const re = pattern
    .split('.')
    .map((tok) => {
      if (tok === '>') return '.+';
      if (tok === '*') return '[^.]+';
      return tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('\\.');
  return new RegExp(`^${re}$`).test(subject);
}

const QUARANTINE_FAILURE_THRESHOLD = 5;
const QUARANTINE_WINDOW_MS = 60_000;
const QUARANTINE_DURATION_MS = 60_000;

interface ErrorCounter {
  count: number;
  windowStart: number;
}

/**
 * Build the MessageHandler closure. The returned function is pure of
 * React; `useNatsSubscription` invokes it directly. State (error
 * counters, quarantine map) lives in the closure — survives across
 * events but resets on provider unmount.
 */
export function createEventDispatcher(deps: DispatcherDeps): MessageHandler {
  const errorCounters = new Map<string, ErrorCounter>();
  const quarantine = new Map<string, number>(); // subject → epoch-ms until

  function recordFailure(subject: string, err: unknown, seq: number, now: number): void {
    deps.onError?.(err, { subject, seq });
    const ctr = errorCounters.get(subject);
    if (ctr === undefined || now - ctr.windowStart > QUARANTINE_WINDOW_MS) {
      errorCounters.set(subject, { count: 1, windowStart: now });
      return;
    }
    ctr.count += 1;
    if (ctr.count >= QUARANTINE_FAILURE_THRESHOLD) {
      quarantine.set(subject, now + QUARANTINE_DURATION_MS);
      errorCounters.delete(subject);
    }
  }

  // Resolve the registry lazily on EVERY dispatch. `deps.registry` is
  // captured at SDKProvider's `useMemo` time, but the identity-flip
  // cleanup (`preReplaceCleanup` → `teardownOldRegistry`) destroys the
  // registry singleton and creates a fresh one. The autoDispatcher's
  // useMemo deps don't include a registry-generation signal — so without
  // this lazy hop, `deps.registry.dispatch()` keeps invoking the OLD
  // destroyed registry's bound method, the NEW registry's `applyEvent`
  // handlers never fire, and the bridge shows `lastSeq` advancing while
  // `dispatch.total` stays at 0 (events arrive on the wire but never
  // touch the cache). Resolving inside the message-handler closure
  // always picks up the current `globalThis.__morfi_registry`.
  function resolveRegistry(): SubjectRegistryApi & {
    isListQueryKeyDispatchAllowed?: (queryHash: string) => boolean;
    dispatch?: (subjectStr: string, seq: number, ev: unknown) => void;
  } {
    try {
      const live = getRegistrySingleton();
      return live as unknown as SubjectRegistryApi & {
        isListQueryKeyDispatchAllowed?: (queryHash: string) => boolean;
        dispatch?: (subjectStr: string, seq: number, ev: unknown) => void;
      };
    } catch {
      return deps.registry as SubjectRegistryApi & {
        isListQueryKeyDispatchAllowed?: (queryHash: string) => boolean;
        dispatch?: (subjectStr: string, seq: number, ev: unknown) => void;
      };
    }
  }

  function applyRoutingEntry(
    subject: string,
    entry: RoutingEntry,
    data: Uint8Array,
    seq: number,
    now: number,
  ): void {
    // Idempotency consult — `patch:append` bypasses (file 05 §L2634).
    if (
      entry.mode !== 'patch:append' &&
      deps.idempotencyLru !== undefined &&
      entry.extractIdempotencyKey !== undefined
    ) {
      const key = entry.extractIdempotencyKey(data);
      if (key !== undefined && deps.idempotencyLru.has(key)) {
        return; // already applied
      }
    }
    // Rebuild the `ctx` per dispatch — the predicate binds to the live
    // registry (see `resolveRegistry` above for why we don't capture the
    // registry once).
    const live = resolveRegistry();
    const ctx: RoutingEntryContext = {
      isListQueryKeyDispatchAllowed: live.isListQueryKeyDispatchAllowed?.bind(live) ?? (() => true),
    };
    try {
      entry.apply(deps.qc, data, subject, seq, ctx);
    } catch (err) {
      recordFailure(subject, err, seq, now);
    }
  }

  return (subject, data, seq) => {
    const now = Date.now();

    // Quarantine gate.
    const quarantineUntil = quarantine.get(subject);
    if (quarantineUntil !== undefined) {
      if (quarantineUntil > now) return;
      quarantine.delete(subject);
    }

    // Optional consumer-provided gate.
    if (deps.routeGate !== undefined && !deps.routeGate(subject)) return;

    // 1. Routing-table fanout (codegen-emitted, default empty).
    // Lookup is two-pass: exact match first, then wildcard. NATS wildcard
    // semantics: `*` matches one token, `>` matches one-or-more trailing.
    // For the hand-wired Stage-1 surface only the trailing wildcards are
    // used (e.g. `morfi.market.*.trade`).
    if (deps.routingTable !== undefined) {
      const exact = deps.routingTable.get(subject);
      if (exact !== undefined) {
        applyRoutingEntry(subject, exact, data, seq, now);
      } else {
        // Wildcard pass — scan keys that contain `*` or `>` and regex-test.
        for (const [pattern, entry] of deps.routingTable) {
          if (!pattern.includes('*') && !pattern.includes('>')) continue;
          if (matchSubject(pattern, subject)) {
            applyRoutingEntry(subject, entry, data, seq, now);
          }
        }
      }
    }

    // 2. Registry-callback fanout. Resolved lazily so a registry replace
    //    (identity flip) routes to the NEW registry's `applyEvent`
    //    handlers, not the destroyed old one.
    try {
      resolveRegistry().dispatch?.(subject, seq, data);
    } catch (err) {
      recordFailure(subject, err, seq, now);
    }
  };
}
